import { withErrorBoundary } from 'app/components/error-boundary';
import { RatingsSection } from 'app/components/rating-card';
import { Tabs } from 'app/components/tabs';
import { Column } from 'app/design/primitives/box';
import { Page, type PageScrollViewRef } from 'app/design/primitives/page';
import { Text } from 'app/design/primitives/text';

import { useNavigationOptions } from 'app/hooks/navigation';
import { withDashboardLayout } from 'app/hooks/with-dashboard-layout';
import { SUPPORT_CHANNEL_URL } from 'app/network/chat/channel';
import { useRatings } from 'app/network/loan';
import { useUser } from 'app/network/user';
import { navigateToListing } from 'app/screens/billboard/navigator-helper';
import { navigateToChat } from 'app/screens/chat/navigator-helper';
import { useTheme } from 'app/shopify';
import type { NavigationScreen } from 'app/types/navigation';
import { isMobileNative, useAlignmentIfDesktop, usePageTitle, useUISize } from 'app/utils/web-helpers';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView } from 'react-native-gesture-handler';

type RatingsTab = 'given' | 'received';

const MyRatingsScreenComponent: NavigationScreen<'my-ratings'> = ({ navigation }) => {
  const { isLargeUI } = useUISize();
  const alignment = useAlignmentIfDesktop();
  const theme = useTheme();
  const { t } = useTranslation();
  const { data: user } = useUser();
  const pageScrollRef = React.useRef<PageScrollViewRef | null>(null);
  const [activeTab, setActiveTab] = useState<RatingsTab>('received');

  const givenRatingsQuery = useRatings({ reviewerId: user?.id }, { enabled: Boolean(user?.id) });
  const receivedRatingsQuery = useRatings({ userId: user?.id }, { enabled: Boolean(user?.id) });

  const title = t('title', { ns: 'my-ratings' });

  useNavigationOptions(navigation, {
    headerTitle: () => (
      <Text variant="bodyXLargeMedium" color="white-label.header.on-header">
        {title}
      </Text>
    ),
  });

  const setPageTitle = usePageTitle('my-ratings');
  useEffect(() => setPageTitle('title'), [setPageTitle]);

  const tabs = [
    {
      label: t('tabs.received', { ns: 'my-ratings', count: receivedRatingsQuery.data?.length ?? 0 }),
      value: 'received',
    },
    {
      label: t('tabs.given', { ns: 'my-ratings', count: givenRatingsQuery.data?.length ?? 0 }),
      value: 'given',
    },
  ] satisfies { label: string; value: RatingsTab }[];

  const activeQuery = activeTab === 'given' ? givenRatingsQuery : receivedRatingsQuery;

  if (activeQuery.isPending) {
    return (
      <Page title={title}>
        <Column
          alignItems="center"
          justifyContent="center"
          style={{ paddingTop: theme.spacing['spacing.general.x12'] }}
        >
          <Text color="text.secondary" variant="bodyBase">
            {t('loading', { ns: 'my-ratings' })}
          </Text>
        </Column>
      </Page>
    );
  }

  if (activeQuery.isError) {
    return (
      <Page title={title}>
        <Column
          alignItems="center"
          justifyContent="center"
          style={{ paddingTop: theme.spacing['spacing.general.x12'] }}
        >
          <Text color="surface.error.default" textAlign="center" variant="bodyBase">
            {activeQuery.error.message}
          </Text>
        </Column>
      </Page>
    );
  }

  return (
    <>
      {!isMobileNative ? (
        <Text
          fontWeight="600"
          marginBottom={isLargeUI ? 'spacing.general.x8' : 'spacing.general.x4'}
          marginTop="spacing.general.x5"
          marginHorizontal="spacing.general.x6"
          textAlign={alignment}
          variant="headerXSmall"
        >
          {title}
        </Text>
      ) : null}

      <Tabs tabs={tabs} value={activeTab} onPress={(tab) => setActiveTab(tab)} />
      <ScrollView showsVerticalScrollIndicator={false}>
        <RatingsSection
          ratings={activeQuery.data}
          subjectName={title}
          memberPerspective={activeTab === 'given' ? 'recipient' : 'reviewer'}
          onPressListing={(id) => navigateToListing(navigation, id)}
          currentUserId={user?.id}
          variant={{
            pendingRatings: 'hidden',
            summary: activeTab === 'given' ? 'hidden' : 'visible',
          }}
          parentScrollRef={pageScrollRef}
          onEditRating={
            activeTab === 'given'
              ? (rating) => {
                  navigation.navigate(
                    'update-rating',
                    {
                      loanId: rating.loanId,
                      listingId: rating.listingId,
                      channelUrl: rating.channelUrl,
                      isLender: rating.reviewerRole === 'lender',
                      subjectName:
                        [rating.ratedUserFirstName, rating.ratedUserLastName].filter(Boolean).join(' ').trim() ||
                        rating.listingTitle ||
                        '',
                      subjectImage: rating.ratedUserPicture ?? null,
                      overallRating: rating.overallRating,
                      assetQualityRating: rating.assetQualityRating,
                      logisticsReliabilityRating: rating.logisticsReliabilityRating,
                      communityTrustRating: rating.communityTrustRating,
                      comment: rating.comment,
                      initialExperience: (rating.overallRating ?? 0) >= 3 ? 'positive' : 'negative',
                    },
                    { pop: true },
                  );
                }
              : undefined
          }
          onNavigateToSupport={() =>
            navigateToChat(navigation, SUPPORT_CHANNEL_URL, {
              i18nKey: 'reportRatingPrompt',
              ns: 'neighbour-profile',
            })
          }
        />
      </ScrollView>
    </>
  );
};

export const MyRatingsScreen: NavigationScreen<'my-ratings'> = withErrorBoundary(
  withDashboardLayout(MyRatingsScreenComponent),
);
